<template>
	<div id="clientDeploy" class="jc-view">
		<JuniperPageHeader
			:back="'/juniper/clients/edit/' + clientId"
			:title="client ? client.name : ''"
			subtitle="Deploy"
		/>

		<ul class="nav nav-tabs mt-3" role="tablist">
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'deployments' }"
					type="button"
					@click="activeTab = 'deployments'"
				>
					<i class="fas fa-list me-1"></i> Deployments
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'settings' }"
					type="button"
					@click="activeTab = 'settings'"
				>
					<i class="fas fa-cog me-1"></i> Settings
				</button>
			</li>
		</ul>

		<div class="cd-tab-content">
			<!-- ====================== DEPLOYMENTS ====================== -->
			<div v-show="activeTab === 'deployments'">
				<div class="cd-layout">
					<aside class="cd-intro">
						<h2 class="cd-intro__title">Recent Deployments</h2>
						<p class="cd-intro__desc">
							History of static builds for this client. Each row shows the engine used, outcome, file count and who triggered it.
						</p>
					</aside>

					<section class="cd-content">
						<div class="cd-card">
							<div class="cd-card__header">
								<select
									v-model="filterStatus"
									class="form-select form-select-sm cd-status-filter"
									@change="loadDeployments(1)"
								>
									<option value="">All statuses</option>
									<option value="success">Success</option>
									<option value="failed">Failed</option>
									<option value="running">Running</option>
								</select>
								<div class="cd-card__header-actions">
									<button
										class="cd-btn cd-btn--sm"
										:disabled="loadingDeployments"
										@click="loadDeployments(page)"
									>
										<i class="fas fa-sync" :class="{ 'fa-spin': loadingDeployments }"></i>
										Reload
									</button>
									<button
										v-if="canBulkDelete"
										class="cd-btn cd-btn--sm cd-btn--danger"
										@click="openPruneModal"
									>
										<i class="fas fa-trash"></i>
										Delete deployments
									</button>
								</div>
							</div>

							<div v-if="loadingDeployments && deployments.length === 0" class="cd-empty">Loading…</div>
							<div v-else-if="deployments.length === 0" class="cd-empty">No deployments yet for this client.</div>
							<div v-else class="table-responsive">
								<table class="table align-middle mb-0 cd-table">
									<thead>
										<tr>
											<th>Started</th>
											<th>Status</th>
											<th class="text-end">Files</th>
											<th class="text-end">Duration</th>
											<th v-if="showCommitColumn">Commit</th>
											<th v-if="showCommitColumn">Release</th>
											<th v-if="showCloudflareColumn">CF deploy</th>
											<th v-if="showR2Column" class="text-end">R2 sync</th>
											<th>Triggered by</th>
										</tr>
									</thead>
									<tbody>
										<template v-for="d in deployments">
											<tr
												:key="d.id"
												class="cd-row"
												@click="openDetailModal(d)"
											>
												<td>
													<div class="cd-status-line">
														<i
															v-if="d.triggered_by_source"
															class="fas cd-row-icon"
															:class="sourceIconClass(d.triggered_by_source)"
															data-bs-toggle="tooltip"
															data-bs-placement="top"
															:title="d.triggered_by_source"
														></i>
														<span class="cd-date">{{ formatLocalTime(d.started_at) }}</span>
													</div>
												</td>
												<td>
													<div class="cd-status-line">
														<span class="cd-engine">{{ d.engine }}</span>
														<i
															class="fas cd-row-icon"
															:class="d.type === 'full-static' ? 'fa-globe' : 'fa-puzzle-piece'"
															data-bs-toggle="tooltip"
															data-bs-placement="top"
															:title="d.type === 'full-static' ? 'full-static — entire site rebuilt' : (d.type || 'part-static') + ' — only changed content rebuilt'"
														></i>
														<i
															class="fas cd-status-icon"
															:class="statusIconClass(d.status)"
															data-bs-toggle="tooltip"
															data-bs-placement="top"
															:title="statusTooltip(d)"
														></i>
														<i
															v-if="d.status === 'success' && d.errors_count > 0"
															class="fas fa-exclamation-triangle cd-status-icon cd-status-icon--warn"
															data-bs-toggle="tooltip"
															data-bs-placement="top"
															:title="deployWarningTooltip(d)"
														></i>
													</div>
												</td>
												<td class="text-end">
													<i class="fas fa-file-alt cd-metric cd-metric--files me-1"></i>
													<span>{{ d.files_count == null ? '—' : d.files_count }}</span>
												</td>
												<td class="text-end">
													<i class="fas fa-clock cd-metric cd-metric--duration me-1"></i>
													<span>{{ d.build_time ? d.build_time + 's' : '—' }}</span>
												</td>
												<td v-if="showCommitColumn">
													<a
														v-if="d.git_commit_url && d.git_commit_sha"
														:href="d.git_commit_url"
														target="_blank"
														rel="noopener"
														class="cd-link-chip cd-link-chip--commit"
														@click.stop
													>
														<i class="fas fa-code-branch me-1"></i>{{ d.git_commit_sha.slice(0, 7) }}
													</a>
													<span v-else class="text-muted">—</span>
												</td>
												<td v-if="showCommitColumn">
													<a
														v-if="d.release_url && d.release_version"
														:href="d.release_url"
														target="_blank"
														rel="noopener"
														class="cd-link-chip cd-link-chip--release"
														@click.stop
													>
														<i class="fas fa-tag me-1"></i>{{ d.release_version }}
													</a>
													<span v-else class="text-muted">—</span>
												</td>
												<td v-if="showCloudflareColumn">
													<a
														v-if="d.cloudflare_deployment_url"
														:href="d.cloudflare_deployment_url"
														target="_blank"
														rel="noopener"
														class="cd-link-chip cd-link-chip--cf"
														@click.stop
													>
														<i class="fas fa-cloud me-1"></i>view
													</a>
													<span v-else class="text-muted">—</span>
												</td>
												<td v-if="showR2Column" class="text-end">
													<template v-if="d.r2_synced_files != null">
														<span
															class="cd-link-chip cd-link-chip--r2"
															data-bs-toggle="tooltip"
															data-bs-placement="top"
															:title="d.r2_synced_files + ' synced, ' + (d.r2_deleted_files || 0) + ' removed'"
														>
															<i class="fas fa-database me-1"></i>{{ d.r2_synced_files }}<span v-if="d.r2_deleted_files"> / -{{ d.r2_deleted_files }}</span>
														</span>
													</template>
													<span v-else class="text-muted">—</span>
												</td>
												<td>
													<template v-if="d.triggered_by">
														{{ d.triggered_by.name || d.triggered_by.email }}
													</template>
													<span v-else class="text-muted">—</span>
												</td>
											</tr>
										</template>
									</tbody>
								</table>
							</div>

							<div v-if="hasMoreDeployments || loadingDeployments" class="cd-card__footer cd-card__footer--centered">
								<button class="cd-link-btn" :disabled="loadingDeployments || !hasMoreDeployments" @click="loadMoreDeployments">
									<i v-if="loadingDeployments" class="fas fa-spinner fa-spin me-1"></i>
									{{ loadingDeployments ? 'Loading…' : 'Load more' }}
								</button>
								<span class="text-muted small">{{ deployments.length }} of {{ pageMeta.total || deployments.length }}</span>
							</div>
						</div>
					</section>
				</div>
			</div>

			<!-- ====================== SETTINGS ====================== -->
			<div v-show="activeTab === 'settings'">
				<!-- ENGINE -->
				<div class="cd-layout">
					<aside class="cd-intro">
						<h2 class="cd-intro__title">Engine</h2>
						<p class="cd-intro__desc">
							Which static-generation engine renders this client's pages. Use Default for the in-process renderer or Juniper Publish Engine for the external engine.
						</p>
					</aside>
					<section class="cd-content">
						<div class="cd-card cd-card--pad">
							<div v-if="loadingSettings" class="text-muted">Loading…</div>
							<div v-else class="cd-field">
								<label>Static generation engine</label>
								<select v-model="form.engine" class="form-select">
									<option value="default">Default Engine</option>
									<option value="juniper-publish-engine">Juniper Publish Engine</option>
								</select>
							</div>
						</div>
					</section>
				</div>

				<hr class="cd-divider" />

				<!-- DEPLOY TARGET -->
				<div class="cd-layout">
					<aside class="cd-intro">
						<h2 class="cd-intro__title">Deploy Target</h2>
						<p class="cd-intro__desc">
							Where the generated static is shipped after each build. Plesk FTP uploads to the subscription, Plesk Git and Cloudflare Pages push to the per-client GitHub repo, Cloudflare R2 syncs the files straight into an R2 bucket served by the juniper-edge Worker.
						</p>
					</aside>
					<section class="cd-content">
						<div class="cd-card cd-card--pad">
							<div class="cd-target-group">
								<label
									class="cd-target-opt"
									:class="{ 'cd-target-opt--active': form.deploy_target === 'plesk_ftp' }"
								>
									<input v-model="form.deploy_target" type="radio" value="plesk_ftp" />
									<i class="fas fa-server"></i>
									<div>
										<div class="cd-target-opt__name">Plesk FTP</div>
										<div class="cd-target-opt__sub">FTP upload to subscription</div>
									</div>
								</label>
								<label
									class="cd-target-opt"
									:class="{ 'cd-target-opt--active': form.deploy_target === 'plesk_git' }"
								>
									<input v-model="form.deploy_target" type="radio" value="plesk_git" />
									<i class="fab fa-github"></i>
									<div>
										<div class="cd-target-opt__name">Plesk Git</div>
										<div class="cd-target-opt__sub">Push, Plesk pulls</div>
									</div>
								</label>
								<label
									class="cd-target-opt"
									:class="{ 'cd-target-opt--active': form.deploy_target === 'cloudflare_pages' }"
								>
									<input v-model="form.deploy_target" type="radio" value="cloudflare_pages" />
									<i class="fas fa-cloud"></i>
									<div>
										<div class="cd-target-opt__name">Cloudflare Pages</div>
										<div class="cd-target-opt__sub">Push, CF auto-deploys</div>
									</div>
								</label>
								<label
									class="cd-target-opt"
									:class="{ 'cd-target-opt--active': form.deploy_target === 'cloudflare_r2' }"
								>
									<input v-model="form.deploy_target" type="radio" value="cloudflare_r2" />
									<i class="fas fa-database"></i>
									<div>
										<div class="cd-target-opt__name">Cloudflare R2</div>
										<div class="cd-target-opt__sub">Sync to bucket, edge Worker serves</div>
									</div>
								</label>
							</div>
						</div>
					</section>
				</div>

				<template v-if="showPleskSection">
				<hr class="cd-divider" />

				<!-- PLESK SERVER -->
				<div class="cd-layout">
					<aside class="cd-intro">
						<h2 class="cd-intro__title">Plesk Server</h2>
						<p class="cd-intro__desc">
							Which Plesk server hosts this client's subscription. Used for FTP/CLI operations like static deploy, suspend/resume, and Git auto-pull.
						</p>
					</aside>
					<section class="cd-content">
						<div class="cd-card cd-card--pad">
							<div class="cd-field">
								<label>Plesk account</label>
								<select v-model="form.plesk_account_name" class="form-select">
									<option :value="null">Use default (first configured)</option>
									<option v-for="acct in pleskAccounts" :key="acct.name" :value="acct.name">
										{{ acct.name }}<span v-if="acct.cli_url"> — {{ shortPleskUrl(acct.cli_url) }}</span>
									</option>
								</select>
								<div v-if="pleskAccounts.length === 0" class="cd-help cd-help--warn">
									<i class="fas fa-exclamation-triangle me-1"></i>
									No Plesk accounts configured —
									<router-link to="/juniper/settings">add one in Juniper Settings</router-link>.
								</div>
							</div>
						</div>
					</section>
				</div>
				</template>

				<template v-if="showGithubSection">
				<hr class="cd-divider" />

				<!-- GIT REPO -->
				<div class="cd-layout">
					<aside class="cd-intro">
						<h2 class="cd-intro__title">GitHub Repository</h2>
						<p class="cd-intro__desc">
							The per-client repo where generated HTML is mirrored. Each client gets its own private repo named after the staging slug.
						</p>
					</aside>
					<section class="cd-content">
						<div class="cd-card cd-card--pad">
							<div class="cd-field">
								<label>GitHub account</label>
								<select
									v-model="form.git_account_name"
									class="form-select"
									:disabled="form.deploy_target === 'plesk'"
								>
									<option :value="null">Use default (first configured)</option>
									<option v-for="acct in githubAccounts" :key="acct.name" :value="acct.name">
										{{ acct.name }}<span v-if="acct.org"> — {{ acct.org }}</span>
									</option>
								</select>
								<div v-if="githubAccounts.length === 0" class="cd-help cd-help--warn">
									<i class="fas fa-exclamation-triangle me-1"></i>
									No accounts configured —
									<router-link to="/juniper/settings">add one in Juniper Settings</router-link>.
								</div>
							</div>

							<div class="cd-repo-row">
								<template v-if="client && client.git_repo_url">
									<div class="cd-repo-info">
										<div class="cd-repo-label">Linked repo</div>
										<a :href="client.git_repo_url" target="_blank" rel="noopener" class="cd-repo-link">
											<i class="fab fa-github me-1"></i>{{ shortRepoUrl(client.git_repo_url) }}
											<i class="fas fa-external-link-alt cd-repo-ext"></i>
										</a>
										<div v-if="client.git_last_pushed_at" class="cd-repo-meta">
											Last push: {{ client.git_last_pushed_at }}
										</div>
									</div>
									<button
										class="cd-btn cd-btn--danger"
										:disabled="deletingRepo"
										title="Delete the GitHub repo for this client"
										@click="confirmDeleteGitRepo"
									>
										<i class="fas" :class="deletingRepo ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
										{{ deletingRepo ? 'Deleting…' : 'Delete repo' }}
									</button>
								</template>
								<template v-else>
									<div class="cd-repo-info">
										<div class="cd-repo-label">No repo linked yet</div>
										<div class="cd-repo-meta">
											Click Create repo to provision a private GitHub repo using the selected account.
										</div>
									</div>
									<button
										class="cd-btn cd-btn--primary"
										:disabled="creatingRepo || githubAccounts.length === 0"
										@click="createRepo"
									>
										<i class="fas" :class="creatingRepo ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
										{{ creatingRepo ? 'Creating…' : 'Create repo' }}
									</button>
								</template>
							</div>

							<!-- Connect to Plesk action only relevant for plesk_git target. -->
							<div v-if="form.deploy_target === 'plesk_git' && client && client.git_repo_url" class="cd-repo-row">
								<div class="cd-repo-info">
									<div class="cd-repo-label">Plesk auto-pull</div>
									<div class="cd-repo-meta">
										Wires the GitHub repo into the Plesk subscription with auto-deploy on push. Re-running refreshes settings (safe to click multiple times).
									</div>
									<div v-if="pleskConnectedAt" class="cd-repo-meta">
										Connected: {{ pleskConnectedAt }}
									</div>
								</div>
								<button
									class="cd-btn"
									:class="pleskConnectedAt ? 'cd-btn--success' : 'cd-btn--primary'"
									:disabled="connectingPlesk"
									@click="connectRepoOnPlesk"
								>
									<i class="fas" :class="plesksButtonIcon"></i>
									{{ plesksButtonLabel }}
								</button>
							</div>

						</div>
					</section>
				</div>
				</template>

				<template v-if="showCloudflareSection">
				<hr class="cd-divider" />

				<!-- CLOUDFLARE PAGES -->
				<div class="cd-layout">
					<aside class="cd-intro">
						<h2 class="cd-intro__title">Cloudflare Pages</h2>
						<p class="cd-intro__desc">
							Auto-deploy the static output via Cloudflare Pages, wired to the GitHub mirror repo. Requires the GitHub repo to exist and at least one Cloudflare account configured in <em>Juniper Settings → Cloudflare</em>.
						</p>
					</aside>
					<section class="cd-content">
						<div class="cd-card cd-card--pad">
							<div class="cd-field">
								<label>Cloudflare account</label>
								<select v-model="form.cloudflare_account_name" class="form-select">
									<option :value="null">Use default (first configured)</option>
									<option v-for="acct in cloudflareAccounts" :key="acct.name" :value="acct.name">
										{{ acct.name }}<span v-if="acct.account_id"> — {{ shortAccountId(acct.account_id) }}</span>
									</option>
								</select>
								<div v-if="cloudflareAccounts.length === 0" class="cd-help cd-help--warn">
									<i class="fas fa-exclamation-triangle me-1"></i>
									No Cloudflare accounts configured —
									<router-link to="/juniper/settings">add one in Juniper Settings</router-link>.
								</div>
							</div>

							<div class="cd-repo-row">
								<template v-if="client && client.cloudflare_pages_url">
									<div class="cd-repo-info">
										<div class="cd-repo-label">Pages project</div>
										<a :href="client.cloudflare_pages_url" target="_blank" rel="noopener" class="cd-repo-link">
											<i class="fas fa-cloud me-1"></i>{{ shortCfUrl(client.cloudflare_pages_url) }}
											<i class="fas fa-external-link-alt cd-repo-ext"></i>
										</a>
										<div v-if="client.cloudflare_pages_created_at" class="cd-repo-meta">
											Created: {{ client.cloudflare_pages_created_at }}
										</div>
									</div>
									<button
										class="cd-btn cd-btn--danger"
										:disabled="deletingPages"
										title="Delete this client's Cloudflare Pages project"
										@click="confirmDeleteCloudflarePagesProject"
									>
										<i class="fas" :class="deletingPages ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
										{{ deletingPages ? 'Deleting…' : 'Delete project' }}
									</button>
								</template>
								<template v-else>
									<div class="cd-repo-info">
										<div class="cd-repo-label">No Pages project yet</div>
										<div class="cd-repo-meta">
											Click <strong>Create Pages project</strong> to provision a Cloudflare Pages project wired to this client's GitHub repo. CF will auto-deploy on each push.
										</div>
									</div>
									<button
										class="cd-btn cd-btn--primary"
										:disabled="creatingPages || !(client && client.git_repo_url)"
										:title="!(client && client.git_repo_url) ? 'Create the GitHub repo first' : ''"
										@click="createCloudflarePagesProject"
									>
										<i class="fas" :class="creatingPages ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
										{{ creatingPages ? 'Creating…' : 'Create Pages project' }}
									</button>
								</template>
							</div>
						</div>
					</section>
				</div>
				</template>

				<template v-if="showR2Section">
				<hr class="cd-divider" />

				<!-- CLOUDFLARE R2 -->
				<div class="cd-layout">
					<aside class="cd-intro">
						<h2 class="cd-intro__title">Cloudflare R2</h2>
						<p class="cd-intro__desc">
							After each build the static output is synced object-by-object into the account's R2 bucket under the client's canonical folder <code>clients/jpNNNNstg.townweb.site/</code>, which the shared <em>juniper-edge</em> Worker serves to both the staging host and the live domain. Saving this target also wires Cloudflare automatically: KV host mapping, Worker route and proxied DNS for each host (live hosts at launch). Requires the R2 keys and the edge KV namespace on the Cloudflare account in <em>Juniper Settings → Cloudflare</em>.
						</p>
					</aside>
					<section class="cd-content">
						<div class="cd-card cd-card--pad">
							<div class="cd-field">
								<label>Cloudflare account</label>
								<select v-model="form.cloudflare_account_name" class="form-select">
									<option :value="null">Use default (first configured)</option>
									<option v-for="acct in cloudflareAccounts" :key="acct.name" :value="acct.name">
										{{ acct.name }}<span v-if="acct.r2_bucket"> — {{ acct.r2_bucket }}</span>
									</option>
								</select>
								<div v-if="cloudflareAccounts.length === 0" class="cd-help cd-help--warn">
									<i class="fas fa-exclamation-triangle me-1"></i>
									No Cloudflare accounts configured —
									<router-link to="/juniper/settings">add one in Juniper Settings</router-link>.
								</div>
								<div v-else-if="selectedCloudflareAccount && !selectedCloudflareAccount.r2_bucket" class="cd-help cd-help--warn">
									<i class="fas fa-exclamation-triangle me-1"></i>
									Account <strong>{{ selectedCloudflareAccount.name }}</strong> has no R2 bucket configured —
									<router-link to="/juniper/settings">fill in the R2 section in Juniper Settings</router-link>.
								</div>
							</div>

							<div class="cd-repo-row">
								<div class="cd-repo-info">
									<div class="cd-repo-label">Bucket folder</div>
									<div class="cd-repo-meta">
										<code>clients/{{ canonicalStagingHost || '…' }}/</code>
										<span v-if="selectedCloudflareAccount && selectedCloudflareAccount.r2_bucket"> in <code>{{ selectedCloudflareAccount.r2_bucket }}</code></span>
									</div>
									<div v-if="client && client.r2_last_synced_at" class="cd-repo-meta">
										Last sync: {{ formatLocalTime(client.r2_last_synced_at) }}
									</div>
									<div v-else class="cd-repo-meta">
										Never synced. Run a full static generation, or click <strong>Sync to R2</strong> to push what is currently on the static disk.
									</div>
								</div>
								<button
									class="cd-btn cd-btn--primary"
									:disabled="syncingR2 || !(client && client.deploy_target === 'cloudflare_r2')"
									:title="!(client && client.deploy_target === 'cloudflare_r2') ? 'Save the Cloudflare R2 target first' : 'Sync the current static output into R2 without regenerating'"
									@click="syncToR2"
								>
									<i class="fas" :class="syncingR2 ? 'fa-spinner fa-spin' : 'fa-sync'"></i>
									{{ syncingR2 ? 'Syncing…' : 'Sync to R2' }}
								</button>
							</div>

							<!-- EDGE HOSTS: what Cloudflare has for each hostname of this client. -->
							<div class="cd-repo-row cd-edge-row">
								<div class="cd-repo-info">
									<div class="cd-repo-label">Edge hosts</div>
									<div v-if="edgeLoading && !edge" class="cd-repo-meta">Checking Cloudflare…</div>
									<div v-else-if="edge && edge.skipped" class="cd-help cd-help--warn">
										<i class="fas fa-exclamation-triangle me-1"></i>{{ edge.message }}
									</div>
									<div v-else-if="edge && edge.hosts && edge.hosts.length" class="table-responsive">
										<table class="table table-sm mb-1 cd-edge-table">
											<thead>
												<tr><th>Host</th><th>Role</th><th>KV</th><th>Route</th><th>DNS</th><th>Zone</th></tr>
											</thead>
											<tbody>
												<tr v-for="h in edge.hosts" :key="h.host">
													<td><code>{{ h.host }}</code></td>
													<td>{{ h.role }}</td>
													<td><span class="cd-edge-badge" :class="edgeBadgeClass(h.kv)">{{ edgeLabel(h.kv) }}</span></td>
													<td><span class="cd-edge-badge" :class="edgeBadgeClass(h.route)">{{ edgeLabel(h.route) }}</span></td>
													<td><span class="cd-edge-badge" :class="edgeBadgeClass(h.dns)">{{ edgeLabel(h.dns) }}</span></td>
													<td class="text-muted">{{ h.zone || (h.route === 'zone_missing' ? 'not in our account' : '') }}<span v-if="h.note" class="d-block small">{{ h.note }}</span><span v-if="h.error" class="d-block small text-danger">{{ h.error }}</span></td>
												</tr>
											</tbody>
										</table>
									</div>
									<div v-else-if="edge" class="cd-repo-meta">{{ edge.message }}</div>
									<div v-else class="cd-repo-meta">Save the Cloudflare R2 target to see the edge wiring for this client's hosts.</div>
								</div>
								<div class="cd-edge-actions">
									<button
										class="cd-btn cd-btn--sm"
										:disabled="edgeLoading || !(client && client.deploy_target === 'cloudflare_r2')"
										@click="loadEdgeStatus"
									>
										<i class="fas" :class="edgeLoading ? 'fa-spinner fa-spin' : 'fa-sync'"></i>
										Refresh
									</button>
									<button
										class="cd-btn cd-btn--primary cd-btn--sm"
										:disabled="wiringEdge || !(client && client.deploy_target === 'cloudflare_r2')"
										:title="!(client && client.deploy_target === 'cloudflare_r2') ? 'Save the Cloudflare R2 target first' : 'Create missing KV entries, routes and DNS records'"
										@click="wireEdge"
									>
										<i class="fas" :class="wiringEdge ? 'fa-spinner fa-spin' : 'fa-plug'"></i>
										{{ wiringEdge ? 'Wiring…' : 'Wire edge hosts' }}
									</button>
								</div>
							</div>
						</div>
					</section>
				</div>
				</template>

				<hr class="cd-divider" />

				<div class="cd-save-row">
					<button class="cd-save-btn" :disabled="savingSettings" @click="saveSettings">
						<i class="fas fa-save"></i>
						{{ savingSettings ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
			</div>
		</div>

		<!-- DELETE CONFIRM MODAL (Bootstrap 5). Moved to <body> in mounted() so a transformed ancestor higher up the tree can't break position:fixed. -->
		<div ref="deleteCfModal" class="modal fade" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="fas fa-exclamation-triangle text-danger me-2"></i>
							{{ pendingDeleteTitle || 'Confirm delete' }}
						</h5>
						<button type="button" class="btn-close" aria-label="Close" @click="closeDeleteModal"></button>
					</div>
					<div class="modal-body">
						<p class="mb-3 text-muted" v-html="pendingDeleteHtml"></p>
						<div class="cd-confirm-row mb-2">
							<span class="text-muted me-2">Type in</span>
							<span class="cd-confirm-chip">
								{{ pendingDeleteName }}
								<button
									type="button"
									class="cd-confirm-chip__copy"
									title="Copy to clipboard"
									@click="copyPendingDeleteName"
								>
									<i class="fas fa-copy"></i>
								</button>
							</span>
							<span class="text-muted ms-2">to confirm</span>
						</div>
						<input
							v-model="deleteConfirmText"
							type="text"
							class="form-control"
							:placeholder="pendingDeleteName"
							autocomplete="off"
							@keyup.enter="executeConfirmedDelete"
						/>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-light" @click="closeDeleteModal">Cancel</button>
						<button
							type="button"
							class="btn btn-danger"
							:disabled="!canConfirmDelete || deletingPages || deletingRepo"
							@click="executeConfirmedDelete"
						>
							<i class="fas me-1" :class="(deletingPages || deletingRepo) ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
							{{ (deletingPages || deletingRepo) ? 'Deleting…' : 'Delete' }}
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- DEPLOYMENT DETAILS MODAL (Bootstrap 5). Row click opens this — same interaction as the ADA reports table. Moved to <body> in mounted(). -->
		<div ref="detailModal" class="modal fade" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
				<div class="modal-content" v-if="detailDeployment">
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="fas fa-rocket me-2 text-muted"></i>
							Deployment — {{ formatLocalTime(detailDeployment.started_at) }}
							<span class="cd-status ms-2" :class="'cd-status--' + detailDeployment.status">{{ detailDeployment.status }}</span>
						</h5>
						<button type="button" class="btn-close" aria-label="Close" @click="closeDetailModal"></button>
					</div>
					<div class="modal-body">
						<div class="cd-detail">
							<div class="cd-detail__grid">
								<div>
									<div class="cd-detail__label">Engine</div>
									<div class="cd-detail__value">
										<code>{{ detailDeployment.engine || '—' }}</code>
										<small class="text-muted ms-1">{{ detailDeployment.type }}</small>
									</div>
								</div>
								<div>
									<div class="cd-detail__label">Files</div>
									<div class="cd-detail__value">{{ detailDeployment.files_count == null ? '—' : detailDeployment.files_count }}</div>
								</div>
								<div>
									<div class="cd-detail__label">Size</div>
									<div class="cd-detail__value">{{ formatBytes(detailDeployment.total_size_bytes) }}</div>
								</div>
								<div>
									<div class="cd-detail__label">Duration</div>
									<div class="cd-detail__value">{{ detailDeployment.build_time ? detailDeployment.build_time + 's' : '—' }}</div>
								</div>
								<div>
									<div class="cd-detail__label">Triggered by</div>
									<div class="cd-detail__value">
										<template v-if="detailDeployment.triggered_by">{{ detailDeployment.triggered_by.name || detailDeployment.triggered_by.email }}</template>
										<span v-else>—</span>
										<small v-if="detailDeployment.triggered_by_source" class="text-muted ms-1">({{ detailDeployment.triggered_by_source }})</small>
									</div>
								</div>
								<div>
									<div class="cd-detail__label">Deploy target</div>
									<div class="cd-detail__value"><code>{{ detailDeployment.deploy_target || '—' }}</code></div>
								</div>
								<div>
									<div class="cd-detail__label">Job ID</div>
									<div class="cd-detail__value">{{ detailDeployment.job_id || '—' }}</div>
								</div>
								<div>
									<div class="cd-detail__label">Engine version</div>
									<div class="cd-detail__value">{{ detailDeployment.engine_version || '—' }}</div>
								</div>
								<div>
									<div class="cd-detail__label">Started</div>
									<div class="cd-detail__value">{{ formatLocalTime(detailDeployment.started_at) || '—' }}</div>
								</div>
								<div>
									<div class="cd-detail__label">Finished</div>
									<div class="cd-detail__value">{{ formatLocalTime(detailDeployment.finished_at) || '—' }}</div>
								</div>
								<div v-if="detailDeployment.plesk_deploy_triggered_at">
									<div class="cd-detail__label">Plesk deploy triggered</div>
									<div class="cd-detail__value">
										{{ formatLocalTime(detailDeployment.plesk_deploy_triggered_at) }}
										<span v-if="detailDeployment.plesk_deploy_ok === true" class="badge bg-success ms-1">ok</span>
										<span v-else-if="detailDeployment.plesk_deploy_ok === false" class="badge bg-danger ms-1">failed</span>
									</div>
								</div>
								<div v-if="detailDeployment.partial_kind">
									<div class="cd-detail__label">Partial</div>
									<div class="cd-detail__value">
										<code>{{ detailDeployment.partial_kind }}</code>
										<small class="text-muted ms-1">{{ detailDeployment.partial_action }} #{{ detailDeployment.partial_entity_id }}</small>
									</div>
								</div>
								<div v-if="detailDeployment.git_commit_url && detailDeployment.git_commit_sha">
									<div class="cd-detail__label">Commit</div>
									<div class="cd-detail__value">
										<a :href="detailDeployment.git_commit_url" target="_blank" rel="noopener" class="cd-link-chip cd-link-chip--commit">
											<i class="fas fa-code-branch me-1"></i>{{ detailDeployment.git_commit_sha.slice(0, 7) }}
										</a>
									</div>
								</div>
								<div v-if="detailDeployment.release_url && detailDeployment.release_version">
									<div class="cd-detail__label">Release</div>
									<div class="cd-detail__value">
										<a :href="detailDeployment.release_url" target="_blank" rel="noopener" class="cd-link-chip cd-link-chip--release">
											<i class="fas fa-tag me-1"></i>{{ detailDeployment.release_version }}
										</a>
									</div>
								</div>
								<div v-if="detailDeployment.r2_synced_files != null">
									<div class="cd-detail__label">R2 sync</div>
									<div class="cd-detail__value">
										{{ detailDeployment.r2_synced_files }} synced
										<small class="text-muted ms-1">{{ detailDeployment.r2_deleted_files || 0 }} removed</small>
									</div>
								</div>
								<div v-if="detailDeployment.cloudflare_deployment_url">
									<div class="cd-detail__label">CF deploy</div>
									<div class="cd-detail__value">
										<a :href="detailDeployment.cloudflare_deployment_url" target="_blank" rel="noopener" class="cd-link-chip cd-link-chip--cf">
											<i class="fas fa-cloud me-1"></i>view
										</a>
									</div>
								</div>
							</div>

							<div v-if="detailDeployment.error_message" class="cd-detail__errors">
								<div class="cd-detail__label">Error</div>
								<pre class="cd-detail__pre">{{ detailDeployment.error_message }}</pre>
							</div>

							<div v-if="Array.isArray(detailDeployment.errors) && detailDeployment.errors.length" class="cd-detail__errors">
								<div class="cd-detail__label">Warnings</div>
								<pre class="cd-detail__pre">{{ detailDeployment.errors.join('\n') }}</pre>
							</div>

							<div v-if="detailDeployment.changes_summary && Object.keys(detailDeployment.changes_summary).length" class="cd-detail__changes">
								<div class="cd-detail__label">Changes</div>
								<pre class="cd-detail__pre">{{ JSON.stringify(detailDeployment.changes_summary, null, 2) }}</pre>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-light" @click="closeDetailModal">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- BULK DELETE DEPLOYMENTS MODAL (Bootstrap 5). Moved to <body> in mounted(), same as the delete-confirm modal. -->
		<div ref="pruneDeploysModal" class="modal fade" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="fas fa-trash text-danger me-2"></i>
							Delete deployments
						</h5>
						<button type="button" class="btn-close" aria-label="Close" @click="closePruneModal"></button>
					</div>
					<div class="modal-body">
						<p class="text-muted mb-3">
							Bulk-delete this client's deployment history rows. Builds still running are never deleted.
							This only clears the history — deployed sites, git commits and releases stay untouched.
						</p>
						<div class="mb-3">
							<label class="form-label">Triggered by</label>
							<select v-model="pruneSource" class="form-select">
								<option value="all">All sources</option>
								<option value="manual">Manual</option>
								<option value="hook">Hook</option>
								<option value="job">Job</option>
								<option value="api">API</option>
							</select>
						</div>
						<div class="mb-3">
							<label class="form-label">Type</label>
							<select v-model="pruneType" class="form-select">
								<option value="all">All types</option>
								<option value="full-static">Full static</option>
								<option value="part-static">Part static</option>
							</select>
						</div>
						<div class="mb-2">
							<label class="form-label">Age</label>
							<select v-model="pruneAge" class="form-select">
								<option value="">Everything (any age)</option>
								<option value="7">Older than 7 days</option>
								<option value="30">Older than 30 days</option>
								<option value="60">Older than 60 days</option>
								<option value="90">Older than 90 days</option>
								<option value="180">Older than 180 days</option>
								<option value="365">Older than 365 days</option>
							</select>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-light" @click="closePruneModal">Cancel</button>
						<button type="button" class="btn btn-danger" :disabled="pruning" @click="executePrune">
							<i class="fas me-1" :class="pruning ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
							{{ pruning ? 'Deleting…' : 'Delete' }}
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- TOAST CONTAINER (top-right). Moved to <body> in mounted() so the position:fixed wrapper isn't clipped by any scroll/transform ancestor. -->
		<div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1100;">
			<div
				ref="cdToast"
				class="toast cd-toast"
				:class="'cd-toast--' + (toastVariant === 'danger' ? 'danger' : 'success')"
				role="alert"
				aria-live="assertive"
				aria-atomic="true"
			>
				<div class="cd-toast__header">
					<i class="fas cd-toast__icon" :class="toastVariant === 'danger' ? 'fa-exclamation-circle' : 'fa-check-circle'"></i>
					<strong class="cd-toast__title">{{ toastVariant === 'danger' ? 'Error' : 'Success' }}</strong>
					<button type="button" class="cd-toast__close" aria-label="Close" @click="hideToast">
						<i class="fas fa-times"></i>
					</button>
				</div>
				<div class="cd-toast__body">{{ toastMessage }}</div>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'
import { formatUtc } from '@/utils/time'
import { Modal, Toast, Tooltip } from 'bootstrap'

export default {
	name: 'ClientDeploy',
	data() {
		return {
			activeTab: 'deployments',
			client: null,

			// Deployments
			deployments: [],
			page: 1,
			perPage: 10,
			pageMeta: { current_page: 1, last_page: 1, total: 0 },
			filterStatus: '',
			loadingDeployments: false,
			// Deployment-details modal (row click)
			detailModal: null,
			detailDeployment: null,

			// Settings
			form: {
				engine: 'default',
				deploy_target: 'plesk',
				git_account_name: null,
				cloudflare_account_name: null,
				plesk_account_name: null,
			},
			githubAccounts: [],
			cloudflareAccounts: [],
			pleskAccounts: [],
			loadingSettings: false,
			savingSettings: false,
			creatingRepo: false,
			deletingRepo: false,
			connectingPlesk: false,
			creatingPages: false,
			deletingPages: false,
			syncingR2: false,
			edge: null,
			edgeLoading: false,
			wiringEdge: false,

			// Delete confirm modal — shared for both GitHub repo and CF Pages.
			deleteModal: null,
			// Bulk-delete deployments modal state
			pruneModal: null,
			pruneSource: 'all',
			pruneType: 'all',
			pruneAge: '',
			pruning: false,
			pendingDeleteName: '',
			pendingDeleteKind: null,   // 'repo' | 'pages'
			pendingDeleteTitle: '',
			pendingDeleteHtml: '',
			deleteConfirmText: '',

			// Bootstrap toast state
			toastInstance: null,
			toastMessage: '',
			toastVariant: 'success',
		}
	},
	computed: {
		clientId() {
			return this.$route.params.id
		},
		// Confirm only enabled when the user types the EXACT project name
		// (case-sensitive, trimmed). Matches the destructive-action pattern
		// Cloudflare/GitHub use — eyeballing the name forces an extra moment
		// of attention so muscle-memory clicks can't wipe a project.
		canConfirmDelete() {
			return (this.deleteConfirmText || '').trim() === (this.pendingDeleteName || '')
				&& this.pendingDeleteName !== ''
		},

		// Conditional table columns based on the client's CURRENT deploy
		// target. Historical rows may have different deploy_target values
		// (we always render the data if present), but column visibility
		// follows the live setting so the table doesn't sprout empty
		// columns when the client uses plesk_ftp.
		showCommitColumn() {
			if (!this.client) return false
			return this.client.deploy_target === 'plesk_git'
				|| this.client.deploy_target === 'cloudflare_pages'
		},
		showCloudflareColumn() {
			if (!this.client) return false
			return this.client.deploy_target === 'cloudflare_pages'
		},
		showR2Column() {
			if (!this.client) return false
			return this.client.deploy_target === 'cloudflare_r2'
		},
		// The client's one R2 folder: "jp" + zero-padded id + "stg.townweb.site".
		// Mirrors Client::canonicalStagingHost() in juniper-api — derived from
		// the id, never from staging_url (legacy clients carry other hosts there).
		canonicalStagingHost() {
			if (!this.client || !this.client.id) return ''
			return 'jp' + String(this.client.id).padStart(4, '0') + 'stg.townweb.site'
		},
		// The Cloudflare account the form currently points at (explicit
		// selection, else the first configured one — same rule as the API).
		selectedCloudflareAccount() {
			if (!this.cloudflareAccounts.length) return null
			if (this.form.cloudflare_account_name) {
				const hit = this.cloudflareAccounts.find(a => a.name === this.form.cloudflare_account_name)
				if (hit) return hit
			}
			return this.cloudflareAccounts[0]
		},
		// Bulk delete is a staff-only action (srdjan/marko).
		canBulkDelete() {
			const email = (this.$store.state.authUser && this.$store.state.authUser.email) || ''
			return email.includes('srdjan') || email.includes('marko')
		},
		hasMoreDeployments() {
			return this.deployments.length < (this.pageMeta.total || 0)
		},

		// Plesk button derived state — green "Connected" if the client row
		// has a plesk_git_connected_at timestamp (set by the backend on a
		// successful --create call). Clears on Delete repo.
		pleskConnectedAt() {
			return (this.client && this.client.plesk_git_connected_at) || null
		},
		plesksButtonLabel() {
			if (this.connectingPlesk) return this.pleskConnectedAt ? 'Refreshing…' : 'Connecting…'
			return this.pleskConnectedAt ? 'Connected to Plesk' : 'Connect Repo on Plesk'
		},
		plesksButtonIcon() {
			if (this.connectingPlesk) return 'fa-spinner fa-spin'
			return this.pleskConnectedAt ? 'fa-check-circle' : 'fa-link'
		},

		// Section visibility derived from deploy target:
		//   plesk_ftp        → Plesk Server only
		//   plesk_git        → Plesk Server + GitHub Repo (Plesk pulls from git)
		//   cloudflare_pages → GitHub Repo + Cloudflare Pages (CF auto-deploys)
		//   cloudflare_r2    → Cloudflare R2 only (bucket sync, edge Worker)
		showPleskSection() {
			return this.form.deploy_target === 'plesk_ftp'
				|| this.form.deploy_target === 'plesk_git'
		},
		showGithubSection() {
			return this.form.deploy_target === 'plesk_git'
				|| this.form.deploy_target === 'cloudflare_pages'
		},
		showCloudflareSection() {
			return this.form.deploy_target === 'cloudflare_pages'
		},
		showR2Section() {
			return this.form.deploy_target === 'cloudflare_r2'
		},
	},
	async mounted() {
		// Load Plesk accounts in parallel with everything else later in this method.
		// (Mounted body continues below — we still do the BS modal/toast setup here.)
		// Bootstrap Modal + Toast: move both elements to <body> before initializing
		// so a transformed/sticky ancestor higher up the tree can't break their
		// position:fixed and leave them invisible. Memory pattern (see
		// settings_ui_pattern memory note about BS5 modals in mc2-app).
		const modalEl = this.$refs.deleteCfModal
		if (modalEl) {
			document.body.appendChild(modalEl)
			this.deleteModal = new Modal(modalEl)
			// Reset the typed confirmation each time the modal closes.
			modalEl.addEventListener('hidden.bs.modal', () => {
				this.deleteConfirmText = ''
			})
		}
		const pruneEl = this.$refs.pruneDeploysModal
		if (pruneEl) {
			document.body.appendChild(pruneEl)
			this.pruneModal = new Modal(pruneEl)
		}
		const detailEl = this.$refs.detailModal
		if (detailEl) {
			document.body.appendChild(detailEl)
			this.detailModal = new Modal(detailEl)
		}
		const toastEl = this.$refs.cdToast
		if (toastEl) {
			const toastContainer = toastEl.closest('.toast-container')
			if (toastContainer) {
				document.body.appendChild(toastContainer)
			}
			// Longer delay (10s) + autohide off for error variant so the user has
			// time to read long Plesk/CF/GitHub error messages without time pressure.
			this.toastInstance = new Toast(toastEl, { delay: 10000, autohide: true })
		}

		await Promise.all([
			this.loadClient(),
			this.loadDeployments(1),
			this.loadGithubAccounts(),
			this.loadCloudflareAccounts(),
			this.loadPleskAccounts(),
		])
	},
	beforeDestroy() {
		// Dispose BS instances and remove the body-attached elements so they
		// don't leak across navigations.
		if (this.deleteModal) {
			this.deleteModal.dispose()
			this.deleteModal = null
		}
		if (this.toastInstance) {
			this.toastInstance.dispose()
			this.toastInstance = null
		}
		const modalEl = this.$refs.deleteCfModal
		if (modalEl && modalEl.parentNode === document.body) {
			document.body.removeChild(modalEl)
		}
		const toastEl = this.$refs.cdToast
		const toastContainer = toastEl && toastEl.closest('.toast-container')
		if (toastContainer && toastContainer.parentNode === document.body) {
			document.body.removeChild(toastContainer)
		}
	},
	methods: {
		// API sends naive UTC timestamps — always render the viewer's local time.
		formatLocalTime(ts) {
			if (!ts) return ''
			return formatUtc(ts, 'MMM D, YYYY h:mm A')
		},

		// ----- Bulk-delete deployments -----
		openPruneModal() {
			if (this.pruneModal) this.pruneModal.show()
		},
		closePruneModal() {
			if (this.pruneModal) this.pruneModal.hide()
		},
		async executePrune() {
			this.pruning = true
			try {
				const payload = { source: this.pruneSource, type: this.pruneType }
				if (this.pruneAge) payload.older_than_days = Number(this.pruneAge)
				const { data } = await juniperApi.post(`/clients/${this.clientId}/static/generations/bulk-delete`, payload)
				const n = data.deleted || 0
				this.showToast(`Deleted ${n} deployment${n === 1 ? '' : 's'}`, 'success')
				this.closePruneModal()
				await this.loadDeployments(1)
			} catch (e) {
				this.showToast((e.response && e.response.data && e.response.data.message) || e.message, 'danger')
			} finally {
				this.pruning = false
			}
		},

		async loadClient() {
			this.loadingSettings = true
			try {
				const { data } = await juniperApi.get('/clients/' + this.clientId)
				this.client = data
				this.form.engine = data.engine || 'default'
				this.form.deploy_target = data.deploy_target || 'plesk'
				this.form.git_account_name = data.git_account_name || null
				this.form.cloudflare_account_name = data.cloudflare_account_name || null
				this.form.plesk_account_name = data.plesk_account_name || null
				if (data.deploy_target === 'cloudflare_r2') {
					this.loadEdgeStatus()
				} else {
					this.edge = null
				}
			} catch (e) {
				console.error('Failed to load client', e)
			} finally {
				this.loadingSettings = false
			}
		},

		// ----- Cloudflare edge wiring (KV / routes / DNS per host) -----
		async loadEdgeStatus() {
			this.edgeLoading = true
			try {
				const { data } = await juniperApi.get('/clients/' + this.clientId + '/r2/edge')
				this.edge = data
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || e.message
				this.edge = { ok: false, message: 'Could not read edge status: ' + msg, hosts: [] }
			} finally {
				this.edgeLoading = false
			}
		},
		async wireEdge() {
			this.wiringEdge = true
			try {
				const { data } = await juniperApi.post('/clients/' + this.clientId + '/r2/edge')
				this.edge = data
				this.showToast(data.message || (data.ok ? 'Edge hosts wired' : 'Edge wiring incomplete'), data.ok ? 'success' : 'danger')
			} catch (e) {
				const body = e && e.response && e.response.data
				if (body && body.hosts) this.edge = body
				this.showToast((body && body.message) || 'Edge wiring failed', 'danger')
			} finally {
				this.wiringEdge = false
			}
		},
		edgeLabel(v) {
			const map = { ok: 'ok', derived: 'derived', created: 'created', proxied: 'proxied', removed: 'removed', missing: 'missing', mismatch: 'mismatch', not_proxied: 'not proxied', zone_missing: 'no zone', skipped: 'at launch', failed: 'failed' }
			return map[v] || (v == null ? '—' : String(v))
		},
		edgeBadgeClass(v) {
			if (['ok', 'derived', 'created', 'proxied', 'removed'].includes(v)) return 'cd-edge-badge--ok'
			if (v === 'skipped') return 'cd-edge-badge--muted'
			if (v === 'zone_missing') return 'cd-edge-badge--warn'
			return 'cd-edge-badge--bad'
		},

		async loadPleskAccounts() {
			try {
				const { data } = await juniperApi.get('/settings/plesk_accounts')
				this.pleskAccounts = Array.isArray(data && data.value) ? data.value : []
			} catch (e) {
				if (!e || !e.response || e.response.status !== 404) {
					console.error('Failed to load Plesk accounts', e)
				}
				this.pleskAccounts = []
			}
		},

		shortPleskUrl(url) {
			if (!url) return ''
			return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
		},

		async loadCloudflareAccounts() {
			try {
				const { data } = await juniperApi.get('/settings/cloudflare_accounts')
				this.cloudflareAccounts = Array.isArray(data && data.value) ? data.value : []
			} catch (e) {
				if (!e || !e.response || e.response.status !== 404) {
					console.error('Failed to load Cloudflare accounts', e)
				}
				this.cloudflareAccounts = []
			}
		},

		// Initial load (or filter change) — replaces the list.
		// Trigger-source icon (manual click / webhook / scheduled job) —
		// the exact source text lives in the tooltip.
		sourceIconClass(source) {
			if (source === 'manual') return 'fa-user'
			if (source === 'hook') return 'fa-bolt'
			if (source === 'job') return 'fa-cogs'
			return 'fa-question-circle'
		},
		// Status icon per deployment status — the row shows just an icon,
		// the details live in the tooltip.
		statusIconClass(status) {
			if (status === 'success') return 'fa-check-circle cd-status-icon--success'
			if (status === 'failed') return 'fa-times-circle cd-status-icon--failed'
			if (status === 'running') return 'fa-spinner fa-spin cd-status-icon--running'
			return 'fa-question-circle'
		},
		statusTooltip(d) {
			if (d.status === 'failed' && d.error_message) {
				return 'failed: ' + this.truncate(d.error_message, 120)
			}
			return d.status
		},
		// Build succeeded but something after it did not (typically the R2
		// sync). First recorded error, so the row explains itself on hover.
		deployWarningTooltip(d) {
			const first = Array.isArray(d.errors) && d.errors.length ? d.errors[0] : null
			return first ? this.truncate(String(first), 160) : (d.errors_count + ' warning(s) — open for details')
		},
		// (Re)attach Bootstrap tooltips on the deployments table. Rows
		// re-render on every load, so stale instances are disposed first.
		initDeployTooltips() {
			this.$nextTick(() => {
				this.$el.querySelectorAll('.cd-table [data-bs-toggle="tooltip"]').forEach((el) => {
					const existing = Tooltip.getInstance(el)
					if (existing) existing.dispose()
					new Tooltip(el)
				})
			})
		},
		async loadDeployments(page = 1) {
			this.loadingDeployments = true
			this.page = page
			try {
				const params = { per_page: this.perPage, page }
				if (this.filterStatus) params.status = this.filterStatus
				const { data } = await juniperApi.get('/clients/' + this.clientId + '/static/generations', { params })
				this.deployments = data.data || []
				this.pageMeta = {
					current_page: data.current_page || (data.meta && data.meta.current_page) || 1,
					last_page: data.last_page || (data.meta && data.meta.last_page) || 1,
					total: data.total || (data.meta && data.meta.total) || (data.data || []).length,
				}
			} catch (e) {
				console.error('Failed to load deployments', e)
				this.deployments = []
				this.pageMeta = { current_page: 1, last_page: 1, total: 0 }
			} finally {
				this.loadingDeployments = false
				this.initDeployTooltips()
			}
		},

		// Load-more pattern — appends the next page to the current list.
		async loadMoreDeployments() {
			if (this.loadingDeployments || !this.hasMoreDeployments) return
			this.loadingDeployments = true
			try {
				const nextPage = this.page + 1
				const params = { per_page: this.perPage, page: nextPage }
				if (this.filterStatus) params.status = this.filterStatus
				const { data } = await juniperApi.get('/clients/' + this.clientId + '/static/generations', { params })
				this.deployments = this.deployments.concat(data.data || [])
				this.page = nextPage
				this.pageMeta = Object.assign({}, this.pageMeta, {
					current_page: data.current_page || nextPage,
					last_page: data.last_page || this.pageMeta.last_page,
					total: data.total || (data.meta && data.meta.total) || this.pageMeta.total,
				})
			} catch (e) {
				console.error('Failed to load more deployments', e)
			} finally {
				this.loadingDeployments = false
				this.initDeployTooltips()
			}
		},

		// Row click — deployment details in a modal (consistent with the ADA
		// reports table, which also opens details in a modal).
		openDetailModal(d) {
			this.detailDeployment = d
			if (this.detailModal) this.detailModal.show()
		},
		closeDetailModal() {
			if (this.detailModal) this.detailModal.hide()
		},

		async loadGithubAccounts() {
			try {
				const { data } = await juniperApi.get('/settings/github_accounts')
				this.githubAccounts = Array.isArray(data && data.value) ? data.value : []
			} catch (e) {
				if (!e || !e.response || e.response.status !== 404) {
					console.error('Failed to load GitHub accounts', e)
				}
				this.githubAccounts = []
			}
		},

		async saveSettings() {
			this.savingSettings = true
			try {
				const payload = Object.assign({}, this.form)
				if (payload.git_account_name === '') payload.git_account_name = null
				if (payload.cloudflare_account_name === '') payload.cloudflare_account_name = null
				if (payload.plesk_account_name === '') payload.plesk_account_name = null
				const { data } = await juniperApi.patch('/clients/' + this.clientId + '/deploy-settings', payload)
				if (this.$toast) this.$toast.success('Deploy settings saved')
				if (data && data.edge) {
					this.edge = data.edge
					this.showToast(data.edge.message || (data.edge.ok ? 'Edge hosts wired' : 'Edge wiring incomplete'), data.edge.ok ? 'success' : 'danger')
				}
				await this.loadClient()
			} catch (e) {
				console.error(e)
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to save settings'
				if (this.$toast) this.$toast.error(msg)
			} finally {
				this.savingSettings = false
			}
		},

		// Push whatever is on the static disk right now into R2 (no regen).
		// Seeds the bucket when a client is first switched to cloudflare_r2
		// and doubles as a "make R2 match GCS" repair action.
		async syncToR2() {
			this.syncingR2 = true
			try {
				const { data } = await juniperApi.post('/clients/' + this.clientId + '/r2/sync')
				if (data && data.ok) {
					this.showToast(data.message || 'Synced to R2', 'success')
					if (data.edge) this.edge = data.edge
					await Promise.all([this.loadClient(), this.loadDeployments(1)])
				} else {
					this.showToast((data && data.message) || 'R2 sync failed', 'danger')
				}
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'R2 sync failed'
				this.showToast(msg, 'danger')
			} finally {
				this.syncingR2 = false
			}
		},

		async createRepo() {
			this.creatingRepo = true
			try {
				const { data } = await juniperApi.post('/clients/' + this.clientId + '/git/create-repo')
				if (data && data.ok) {
					this.showToast(data.message || 'Repo ready', 'success')
					await this.loadClient()
				} else {
					this.showToast((data && data.message) || 'Failed to create repo', 'danger')
				}
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to create repo'
				this.showToast(msg, 'danger')
			} finally {
				this.creatingRepo = false
			}
		},

		formatBytes(bytes) {
			if (bytes === null || bytes === undefined) return '—'
			if (bytes < 1024) return bytes + ' B'
			if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
			return (bytes / 1024 / 1024).toFixed(1) + ' MB'
		},
		truncate(s, n) {
			if (!s) return ''
			return s.length > n ? s.slice(0, n) + '…' : s
		},
		shortRepoUrl(url) {
			if (!url) return ''
			return url.replace(/^https?:\/\/(www\.)?github\.com\//, '')
		},
		shortCfUrl(url) {
			if (!url) return ''
			return url.replace(/^https?:\/\//, '')
		},
		shortAccountId(id) {
			if (!id) return ''
			if (id.length <= 12) return id
			return id.slice(0, 6) + '…' + id.slice(-4)
		},

		async createCloudflarePagesProject() {
			this.creatingPages = true
			try {
				const { data } = await juniperApi.post('/clients/' + this.clientId + '/cloudflare/create-project')
				if (data && data.ok) {
					this.showToast(data.message || 'Pages project ready', 'success')
					await this.loadClient()
				} else {
					this.showToast((data && data.message) || 'Failed to create Pages project', 'danger')
				}
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to create Pages project'
				this.showToast(msg, 'danger')
			} finally {
				this.creatingPages = false
			}
		},

		// ---- Shared delete-confirm modal ----
		openDeleteModal(kind, name, title, html) {
			this.pendingDeleteKind = kind
			this.pendingDeleteName = name
			this.pendingDeleteTitle = title
			this.pendingDeleteHtml = html
			this.deleteConfirmText = ''
			if (this.deleteModal) this.deleteModal.show()
		},
		closeDeleteModal() {
			if (this.deleteModal) this.deleteModal.hide()
		},
		executeConfirmedDelete() {
			if (!this.canConfirmDelete) return
			if (this.pendingDeleteKind === 'pages') {
				this.executeDeleteCloudflarePagesProject()
			} else if (this.pendingDeleteKind === 'repo') {
				this.executeDeleteGitRepo()
			}
		},

		// ---- Cloudflare Pages delete ----
		confirmDeleteCloudflarePagesProject() {
			if (!this.client || !this.client.cloudflare_pages_project) {
				this.showToast('No project to delete', 'danger')
				return
			}
			const name = this.client.cloudflare_pages_project
			this.openDeleteModal(
				'pages',
				name,
				'Delete Cloudflare Pages project',
				'Deleting <strong>' + name + '</strong> is permanent and cannot be undone. The GitHub repo is not affected.'
			)
		},
		async executeDeleteCloudflarePagesProject() {
			this.deletingPages = true
			try {
				const { data } = await juniperApi.delete('/clients/' + this.clientId + '/cloudflare/delete-project')
				if (data && data.ok) {
					this.showToast(data.message || 'Pages project deleted', 'success')
					await this.loadClient()
				} else {
					this.showToast((data && data.message) || 'Failed to delete Pages project', 'danger')
				}
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to delete Pages project'
				this.showToast(msg, 'danger')
			} finally {
				this.deletingPages = false
				this.closeDeleteModal()
			}
		},

		// ---- GitHub repo delete ----
		confirmDeleteGitRepo() {
			if (!this.client || !this.client.git_repo_name) {
				this.showToast('No repo to delete', 'danger')
				return
			}
			const name = this.client.git_repo_name
			this.openDeleteModal(
				'repo',
				name,
				'Delete GitHub repository',
				'Deleting <strong>' + name + '</strong> from GitHub is permanent and cannot be undone. Cloudflare Pages or Plesk Git connections that point to this repo will stop working.'
			)
		},
		async executeDeleteGitRepo() {
			this.deletingRepo = true
			try {
				const { data } = await juniperApi.delete('/clients/' + this.clientId + '/git/delete-repo')
				if (data && data.ok) {
					this.showToast(data.message || 'Repo deleted', 'success')
					await this.loadClient()
				} else {
					this.showToast((data && data.message) || 'Failed to delete repo', 'danger')
				}
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to delete repo'
				this.showToast(msg, 'danger')
			} finally {
				this.deletingRepo = false
				this.closeDeleteModal()
			}
		},

		// ---- Plesk Git auto-deploy connection ----
		// Calls Plesk's built-in Git extension via /api/v2/cli/extension/call
		// with `--call git --create -domain ... -remote-url ... -deployment-mode auto_deployment`.
		// On success, backend stamps `clients.plesk_git_connected_at` so the
		// UI button flips to a green "Connected to Plesk" state.
		async connectRepoOnPlesk() {
			this.connectingPlesk = true
			try {
				const { data } = await juniperApi.post('/clients/' + this.clientId + '/plesk/connect-git-repo')
				if (data && data.ok) {
					this.showToast(data.message || 'Repo connected on Plesk', 'success')
					await this.loadClient()
				} else {
					this.showToast((data && data.message) || 'Failed to connect repo on Plesk', 'danger')
				}
			} catch (e) {
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to connect repo on Plesk'
				this.showToast(msg, 'danger')
			} finally {
				this.connectingPlesk = false
			}
		},

		async copyPendingDeleteName() {
			const name = this.pendingDeleteName
			if (!name) return
			try {
				if (navigator.clipboard && navigator.clipboard.writeText) {
					await navigator.clipboard.writeText(name)
				} else {
					// Fallback for non-HTTPS dev contexts where the Clipboard API is missing.
					const tmp = document.createElement('textarea')
					tmp.value = name
					document.body.appendChild(tmp)
					tmp.select()
					document.execCommand('copy')
					document.body.removeChild(tmp)
				}
				this.showToast('Copied: ' + name, 'success')
			} catch (e) {
				this.showToast('Could not copy to clipboard', 'danger')
			}
		},

		// -- Bootstrap toast helper --
		showToast(message, variant) {
			this.toastMessage = message || ''
			this.toastVariant = variant === 'danger' ? 'danger' : 'success'
			if (this.toastInstance) {
				this.toastInstance.show()
			}
		},
		hideToast() {
			if (this.toastInstance) {
				this.toastInstance.hide()
			}
		},
	},
}
</script>

<style scoped lang="scss">
.cd-tab-content {
	padding: 15px;
}

.cd-layout {
	display: grid;
	grid-template-columns: 30% 1fr;
	gap: 1.5rem;
	align-items: start;
}

@media (max-width: 991px) {
	.cd-layout {
		grid-template-columns: 1fr;
	}
}

.cd-intro {
	padding: 0.25rem 1rem 0 0;
}

.cd-intro__title {
	font-size: 1.4rem;
	font-weight: 700;
	color: #2b303b;
	margin: 0 0 0.5rem;
	letter-spacing: -0.01em;
}

.cd-intro__desc {
	font-size: 0.85rem;
	color: #8a909c;
	line-height: 1.5;
	margin: 0;
}

.cd-content {
	display: flex;
	flex-direction: column;
}

.cd-card {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	background: transparent;
	overflow: hidden;
}

.cd-card--pad {
	padding: 1.15rem;
}

.cd-card__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.7rem 1rem;
	border-bottom: 1px solid #f0f1f4;
	gap: 0.5rem;
}

.cd-status-filter {
	width: auto;
}

.cd-card__footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.65rem 1rem;
	border-top: 1px solid #f0f1f4;
	background: #f8f9fb;
}

/* Load more — one compact centered line: [Load more] 30 of 94 */
.cd-card__footer--centered {
	justify-content: center;
	gap: 0.75rem;
}

/* Header actions (Reload / Delete deployments) grouped on the right */
.cd-card__header-actions {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.cd-btn--danger {
	color: #dc2626;
	border-color: #fecaca;
}

.cd-btn--danger:hover:not(:disabled) {
	background: #fee2e2;
	border-color: #fca5a5;
	color: #dc2626;
}

.cd-empty {
	padding: 2rem 1rem;
	text-align: center;
	color: #8a909c;
}

/* Compact flat table — same design as the settings Hooks "Recent activity"
   list: plain 13px rows, thin separators, no header background. */
.cd-table {
	font-size: 13px;
	margin-bottom: 0;
}

.cd-table thead th {
	font-size: 13px;
	font-weight: 600;
	text-transform: none;
	letter-spacing: 0;
	color: #6c757d;
	padding: 4px 10px 4px 0.5rem;
	border-bottom: 1px solid #dee2e6;
	background: transparent;
}

.cd-table tbody td {
	padding: 6px 10px 6px 0.5rem;
	border-color: #f1f3f5;
	vertical-align: middle;
}

.cd-table tbody tr:last-child td {
	border-bottom: none;
}

.cd-code {
	font-size: 0.78rem;
	background: #f3f5f9;
	padding: 0.1rem 0.4rem;
	border-radius: 5px;
	color: #4a5260;
}

/* Coloured metric icons in deployment rows — soft accent tones so the
   numbers next to them stay the primary content. */
.cd-metric {
	font-size: 0.78rem;
	opacity: 0.85;
}
.cd-metric--files    { color: #8a909c; } /* neutral gray */
.cd-metric--size     { color: #a855f7; } /* purple     */
.cd-metric--duration { color: #8a909c; } /* neutral gray */

/* Inline link chips in the per-row deploy detail columns (commit, release,
   CF deploy). Look like clickable badges but don't shout. */
.cd-link-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.1rem 0.45rem;
	border-radius: 5px;
	font-size: 0.78rem;
	font-weight: 600;
	text-decoration: none;
	border: 1px solid transparent;
	transition: background 0.12s ease, color 0.12s ease;
}
.cd-link-chip:hover { text-decoration: none; }

.cd-link-chip--commit {
	background: #fff4e6;
	color: #b25608;
	border-color: #f6d3a8;
}
.cd-link-chip--commit:hover { background: #fde7c8; color: #8a3f04; }

.cd-link-chip--release {
	background: #f5edff;
	color: #6b21a8;
	border-color: #d8c2ee;
}
.cd-link-chip--release:hover { background: #ecdcff; color: #4c1d95; }

.cd-link-chip--cf {
	background: #fff1e6;
	color: #c2410c;
	border-color: #f7c8a6;
}
.cd-link-chip--cf:hover { background: #ffe0c4; color: #9a3412; }
.cd-link-chip--r2 {
	background: #ecfeff;
	color: #0e7490;
	cursor: default;
}
.cd-link-chip--r2:hover { background: #cffafe; color: #155e75; }
.cd-status-icon--warn { color: #d97706; }

/* Edge hosts table (Cloudflare KV / route / DNS per host). */
.cd-edge-row { align-items: flex-start; }
.cd-edge-row .cd-repo-info { flex: 1 1 auto; min-width: 0; }
.cd-edge-actions { display: flex; flex-direction: column; gap: 6px; flex: 0 0 auto; }
.cd-edge-table { font-size: 0.85rem; }
.cd-edge-table th { font-weight: 600; color: #6b7280; white-space: nowrap; }
.cd-edge-badge {
	display: inline-block;
	padding: 1px 8px;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 600;
	white-space: nowrap;
}
.cd-edge-badge--ok { background: #dcfce7; color: #166534; }
.cd-edge-badge--bad { background: #fee2e2; color: #991b1b; }
.cd-edge-badge--warn { background: #fef3c7; color: #92400e; }
.cd-edge-badge--muted { background: #f3f4f6; color: #6b7280; }

/* Row click → expand. Hover state hints clickability, chevron rotates when open. */
.cd-row { cursor: pointer; }
.cd-row:hover td { background: #eef2f7; }

.cd-row__chev {
	color: #8a909c;
	width: 28px;
	text-align: center;
}
.cd-row__chev i { transition: transform 0.15s ease; }
.cd-row--open .cd-row__chev i { transform: rotate(90deg); color: #2b303b; }

.cd-row__detail td {
	background: #f8f9fb;
	border-top: none;
}

/* Expanded-row detail panel: grid of label/value pairs + optional preformatted
   error / changes blocks. */
.cd-detail {
	padding: 0.85rem 1rem;
}
.cd-detail__grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	gap: 0.85rem 1.5rem;
	margin-bottom: 0.75rem;
}
.cd-detail__label {
	font-size: 0.7rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #8a909c;
	margin-bottom: 0.15rem;
}
.cd-detail__value {
	font-size: 0.85rem;
	color: #2b303b;
}
.cd-detail__errors,
.cd-detail__changes {
	margin-top: 0.75rem;
}
.cd-detail__pre {
	font-size: 0.72rem;
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 6px;
	padding: 0.6rem 0.75rem;
	max-height: 240px;
	overflow: auto;
	color: #4a5260;
	margin: 0.35rem 0 0;
	// Long error messages and JSON dumps should wrap to multiple lines
	// instead of overflowing horizontally and getting clipped under the
	// table's right edge. `pre-wrap` preserves line breaks but folds long
	// lines; `overflow-wrap: anywhere` breaks inside paths/URLs that have
	// no spaces (e.g. "themes/default/business-directory/single/default.php").
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	word-break: break-word;
}

/* Merged status cell: engine name + type icon + status icon in one line.
   The status icon sits a bit further apart, details are in tooltips. */
.cd-status-line {
	display: flex;
	align-items: center;
	gap: 0.45rem;
	white-space: nowrap;
}

/* Shared row icon — same size/tone as the files metric icon so every
   icon in a row lines up visually. */
.cd-row-icon {
	font-size: 0.78rem;
	opacity: 0.85;
	color: #8a909c;
	cursor: help;
}

.cd-status-icon {
	font-size: 0.78rem;
	opacity: 0.9;
	cursor: help;
}

.cd-date {
	font-size: 12.5px;
	color: #2b303b;
}

.cd-engine {
	font-size: 13px;
	color: #4a5260;
}

.cd-status-icon--success {
	color: #1ec872;
}

.cd-status-icon--failed {
	color: #c93434;
}

.cd-status-icon--running {
	color: #c97300;
}

.cd-status {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	padding: 0.15rem 0.5rem;
	border-radius: 5px;
	display: inline-block;
}

.cd-status--success {
	background: #e6f8ef;
	color: #117a45;
}

.cd-status--failed {
	background: #ffe9e9;
	color: #c93434;
}

.cd-status--running {
	background: #fff3e0;
	color: #c97300;
}

.cd-field {
	margin-bottom: 1rem;
}

.cd-field:last-child {
	margin-bottom: 0;
}

.cd-field label {
	display: block;
	font-size: 0.78rem;
	font-weight: 600;
	color: #4a5260;
	margin-bottom: 0.35rem;
}

.cd-field ::v-deep .form-select,
.cd-field ::v-deep .form-control {
	border-radius: 9px;
	border-color: #e6e8ec;
	font-size: 0.9rem;
	padding: 0.5rem 0.75rem;
}

.cd-field ::v-deep .form-select:focus,
.cd-field ::v-deep .form-control:focus {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}

.cd-help {
	font-size: 0.75rem;
	color: #8a909c;
	margin-top: 0.4rem;
}

.cd-help--warn {
	color: #c97300;
}

.cd-help--warn a {
	color: #c97300;
	font-weight: 600;
}

.cd-target-group {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.65rem;
}

@media (max-width: 767px) {
	.cd-target-group {
		grid-template-columns: 1fr;
	}
}

.cd-target-opt {
	display: flex;
	align-items: center;
	gap: 0.7rem;
	padding: 0.85rem 0.9rem;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	background: transparent;
	cursor: pointer;
	user-select: none;
	transition: all 0.12s ease;
	margin: 0;
}

.cd-target-opt input {
	display: none;
}

.cd-target-opt i {
	font-size: 1.1rem;
	color: #8a909c;
	width: 22px;
	text-align: center;
}

.cd-target-opt:hover {
	background: #f3f5f9;
}

.cd-target-opt:hover i {
	color: #2b303b;
}

.cd-target-opt--active {
	border-color: #b6cfff;
	background: #ebf2ff;
}

.cd-target-opt--active i {
	color: #2b6cff;
}

.cd-target-opt--active .cd-target-opt__name {
	color: #2b6cff;
}

.cd-target-opt__name {
	font-size: 0.9rem;
	font-weight: 600;
	color: #2b303b;
}

.cd-target-opt__sub {
	font-size: 0.72rem;
	color: #8a909c;
}

.cd-repo-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding: 0.85rem 1rem;
	background: #f8f9fb;
	border-radius: 10px;
	margin: 1rem 0;
}

.cd-repo-info {
	min-width: 0;
	flex: 1;
}

.cd-repo-label {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: #8a909c;
	margin-bottom: 0.2rem;
}

.cd-repo-link {
	font-size: 0.95rem;
	font-weight: 600;
	color: #2b303b;
	text-decoration: none;
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
}

.cd-repo-link:hover {
	color: #2b6cff;
}

.cd-repo-ext {
	font-size: 0.7rem;
	opacity: 0.5;
}

.cd-repo-meta {
	font-size: 0.75rem;
	color: #8a909c;
	margin-top: 0.2rem;
}

.cd-toggle {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	padding: 0.85rem 1rem;
	background: #f8f9fb;
	border-radius: 10px;
}

.cd-toggle__label {
	font-weight: 600;
	font-size: 0.9rem;
	color: #2b303b;
}

.cd-toggle__help {
	font-size: 0.75rem;
	color: #8a909c;
	margin-top: 0.15rem;
}

.cd-switch {
	position: relative;
	display: inline-block;
	width: 44px;
	height: 24px;
	flex-shrink: 0;
	margin: 0;
}

.cd-switch input {
	opacity: 0;
	width: 0;
	height: 0;
}

.cd-switch__slider {
	position: absolute;
	inset: 0;
	background: #cbd0d8;
	border-radius: 24px;
	cursor: pointer;
	transition: 0.2s;
}

.cd-switch__slider::before {
	content: '';
	position: absolute;
	width: 18px;
	height: 18px;
	left: 3px;
	bottom: 3px;
	background: #fff;
	border-radius: 50%;
	transition: 0.2s;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.cd-switch input:checked + .cd-switch__slider {
	background: #1ec872;
}

.cd-switch input:checked + .cd-switch__slider::before {
	transform: translateX(20px);
}

.cd-switch input:disabled + .cd-switch__slider {
	opacity: 0.4;
	cursor: not-allowed;
}

.cd-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.45rem 0.9rem;
	border: 1px solid #e6e8ec;
	border-radius: 9px;
	font-size: 0.85rem;
	font-weight: 600;
	background: transparent;
	color: #2b303b;
	cursor: pointer;
	transition: all 0.12s ease;
}

.cd-btn:hover:not(:disabled) {
	background: #f3f5f9;
}

.cd-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

/* Compact variant for table toolbars — follows the flat table design. */
.cd-btn--sm {
	padding: 0.25rem 0.6rem;
	font-size: 0.78rem;
	border-radius: 7px;
	gap: 0.3rem;
}

/* Plain text action — no border/background/padding, gray like the delete
   icon in the Hooks "Recent activity" table. */
.cd-link-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	background: none;
	border: 0;
	padding: 0;
	color: #adb5bd;
	font-size: 0.85rem;
	font-weight: 600;
	cursor: pointer;
	transition: color 0.12s ease;
}

.cd-link-btn:hover:not(:disabled) {
	color: #2b303b;
}

.cd-link-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

.cd-btn--primary {
	background: #2b6cff;
	border-color: #2b6cff;
	color: #fff;
}

.cd-btn--primary:hover:not(:disabled) {
	background: #2057db;
	border-color: #2057db;
}

.cd-btn--success {
	background: #1ec872;
	border-color: #1ec872;
	color: #fff;
}

.cd-btn--success:hover:not(:disabled) {
	background: #17a45c;
	border-color: #17a45c;
}

.cd-btn--danger {
	color: #c93434;
	border-color: #f5c2c2;
}

.cd-btn--danger:hover:not(:disabled) {
	background: #ffe9e9;
	border-color: #ec9b9b;
}

.cd-icon-btn {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	border: 1px solid #e6e8ec;
	background: transparent;
	color: #4a5260;
	cursor: pointer;
	transition: all 0.12s ease;
}

.cd-icon-btn--danger {
	color: #dc2626;
}

.cd-icon-btn--danger:hover:not(:disabled) {
	background: #fee2e2 !important;
	border-color: #fecaca !important;
	color: #dc2626 !important;
}

.cd-icon-btn:hover:not(:disabled) {
	background: #f3f5f9;
	color: #2b303b;
}

.cd-icon-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.cd-divider {
	border: none;
	border-top: 1px solid #c8ccd4;
	margin: 1.5rem 15px;
}

.cd-save-row {
	display: flex;
	justify-content: flex-end;
}

.cd-save-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.35rem 0.9rem;
	background: transparent;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	color: #2b303b;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.15s ease;
}

.cd-save-btn:hover:not(:disabled) {
	background: #f3f5f9;
}

.cd-save-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

/* Soft, pastel-styled toasts — easier on the eyes than Bootstrap's
   saturated text-bg-* utilities. Color is conveyed by left border + icon +
   title hue; background stays light so the message is legible. */
.cd-toast {
	max-width: 520px;
	width: max-content;
	min-width: 320px;
	border: 1px solid #e6e8ec;
	border-left-width: 4px;
	border-radius: 10px;
	overflow: hidden;
	background: #fff;
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.cd-toast--success {
	background: #f0faf3;
	border-color: #c8ecd5;
	border-left-color: #1ec872;
}
.cd-toast--success .cd-toast__icon,
.cd-toast--success .cd-toast__title { color: #117a45; }

.cd-toast--danger {
	background: #fdecea;
	border-color: #f5c2c2;
	border-left-color: #c93434;
}
.cd-toast--danger .cd-toast__icon,
.cd-toast--danger .cd-toast__title { color: #a02020; }

.cd-toast__header {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 0.75rem 0.1rem;
}

.cd-toast__icon {
	font-size: 0.95rem;
}

.cd-toast__title {
	font-size: 0.85rem;
	flex: 1;
	letter-spacing: 0.01em;
}

.cd-toast__close {
	background: transparent;
	border: none;
	padding: 0.15rem 0.35rem;
	color: #2b303b;
	font-size: 0.85rem;
	border-radius: 4px;
	cursor: pointer;
	opacity: 0.65;
	transition: opacity 0.12s ease, background 0.12s ease;
}

.cd-toast__close:hover {
	opacity: 1;
	background: rgba(0, 0, 0, 0.06);
}

.cd-toast__body {
	padding: 0 0.75rem 0.6rem 2.15rem;
	font-size: 0.85rem;
	color: #2b303b;
	word-break: break-word;
	white-space: pre-wrap;
	line-height: 1.4;
}

/* Delete-confirm modal: inline row with the resource name shown as a soft
   chip + copy button. Mirrors the Cloudflare/GitHub "type the name" pattern. */
.cd-confirm-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	font-size: 0.9rem;
}

.cd-confirm-chip {
	display: inline-flex;
	align-items: center;
	gap: 0.45rem;
	padding: 0.15rem 0.5rem;
	background: #f3f5f9;
	border: 1px solid #e6e8ec;
	border-radius: 6px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
	font-size: 0.85rem;
	color: #2b303b;
	font-weight: 600;
}

.cd-confirm-chip__copy {
	background: transparent;
	border: none;
	padding: 0;
	color: #8a909c;
	font-size: 0.75rem;
	line-height: 1;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	transition: color 0.12s ease;
}

.cd-confirm-chip__copy:hover {
	color: #2b303b;
}
</style>
